// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid2, Stack, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseInput, BaseSelect, BaseUploadImage } from '~/components/common';
// Others
import { TypeCrmEnum } from '~/utils/enum/common';
import { IFormClient } from '~/utils/interface/client';
import { optionBusinessTypeClient, optionPaymentTerms } from '~/utils/constants/common';
import { IBaseFile, IBaseOption, IResultCallApiFn } from '~/utils/interface/common';
// Styles, images, icons
import styles from './PersonalInfoClientForm.module.scss';

type Props = {
  typeList: string;
  onDeleteImage?: (remove: boolean) => void;
};

const cx = classNames.bind(styles);

const PersonalInfoClientForm = (props: Props) => {
  //#region Destructuring Props
  const { typeList, onDeleteImage } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);
  const { id: clientId } = useParams();

  const {
    control,
    formState: { errors },
  } = useFormContext<IFormClient>();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const fetchOptionsType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionBusinessTypeClient,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsPaymentTerms = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionPaymentTerms,
      hasMoreResp: false,
    };

    return result;
  }, []);
  //#endregion Handle Function

  return (
    <Stack id='personalInfoClientFormComponent' gap={'14px'}>
      <Typography className={cx('title')}>{t('client_form_title_personal_information')}</Typography>

      <Grid2 container rowSpacing={3} columnSpacing={5}>
        {typeList === TypeCrmEnum.CLIENT && (
          <Grid2 size={12}>
            <Controller
              control={control}
              name={'avatarImage'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseUploadImage
                    label={t('client_form_title_photo')}
                    {...(clientId && !value?.file && { uploadedFiles: [value || {}] })}
                    onChange={(files: IBaseFile[]) => onChange(files?.[0])}
                    errorMessage={errors.avatarImage?.message}
                    isMultiple={false}
                    isButton={false}
                    height={130}
                    onRemove={() => onDeleteImage?.(true)}
                  />
                );
              }}
            />
          </Grid2>
        )}

        <Grid2 size={6}>
          <Controller
            control={control}
            name={'firstName'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('client_form_title_first_name')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.firstName?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            control={control}
            name={'lastName'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('client_form_title_last_name')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.lastName?.message}
                />
              );
            }}
          />
        </Grid2>

        {typeList === TypeCrmEnum.CLIENT && (
          <>
            <Grid2 size={6}>
              <Controller
                control={control}
                name={'businessType'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseSelect
                      gap={10}
                      height={36}
                      styleType='contained'
                      label={t('client_form_title_type')}
                      callApiFn={fetchOptionsType}
                      value={value}
                      onChange={(data) => onChange(data?.value)}
                      errorMessage={errors?.businessType?.message}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'companyName'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      height={36}
                      label={t('client_form_title_entity_name')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.companyName?.message}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'paymentTerms'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseSelect
                      gap={10}
                      height={36}
                      styleType='contained'
                      label={t('client_form_title_payment_terms')}
                      callApiFn={fetchOptionsPaymentTerms}
                      value={value}
                      onChange={(data) => onChange(data?.value)}
                      errorMessage={errors?.paymentTerms?.message}
                    />
                  );
                }}
              />
            </Grid2>
          </>
        )}
        {typeList === TypeCrmEnum.LEAD && (
          <>
            <Grid2 size={6}>
              <Controller
                control={control}
                name={'companyName'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      height={36}
                      label={t('client_form_title_entity_name')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.companyName?.message}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'title'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      height={36}
                      label={t('client_form_title_title')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.title?.message}
                    />
                  );
                }}
              />
            </Grid2>
          </>
        )}
      </Grid2>
    </Stack>
  );
};

export default PersonalInfoClientForm;
